import { useTranslation } from "react-i18next";
import { RootState } from "../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { calculateDuration } from "../../../../util/date";
import moment from "moment";
import { atStation } from "../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";
import { useState, useEffect, useMemo, useCallback, memo, useId } from "react";
import { useApplyCoupon } from "../../../../redux/flows/applyCouponFlow";
import {
  calculateDisplayPrice,
  calculateTotalPrice,
  consolidateCoupons,
  findCoupons,
  isFreeOfCharge,
  requiresPayment,
} from "../../../../util/payments";
import { setNextButtonClick } from "../../../../redux/slice/temporaryStorage/temporaryStorageSlice";
import { formatCurrency, getEquipmentName } from "../../../../util";
import { couponRedemptionLoading, emailChanged, firstNameChanged, lastNameChanged } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { cn } from "lib/utils";
import { getEmailAndFirstAndLastName, getIdToken } from "../../../../authentication/Authorizer";
import { i18n } from "../../../../i18n/i18n";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Spinner, SpinnerOverlay } from "components/ui/spinner";
import { useTemporaryStorageStepCounter } from "../hooks/useTemporaryStorageStepCounter";
import { bookedItemLabel } from "../../common/bookedItemLabel";
import { useTaxStatement } from "../../../../util/taxStatement";
import { loginSuppliesContactData } from "../../../../authentication/loginSuppliesContactData";

/**
 * Renders decorative ticket hole punch effect on the side of the ticket
 */
const TicketHole = ({ side, uniqueId }: { side: "left" | "right"; uniqueId: string }) => {
  const clipPathId = `${side}Hole-${uniqueId}`;
  const isRight = side === "right";
  
  return (
    <svg 
      className={cn(
        "absolute top-[29%] w-4 h-4 z-10 pointer-events-none",
        isRight ? "right-[-8px]" : "left-[-8px]"
      )} 
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clipPathId}>
          <rect x={isRight ? "0" : "8"} y="0" width="8" height="16" />
        </clipPath>
      </defs>
      {/* White rectangle to cover the border at the hole */}
      <rect x={isRight ? "5" : "7"} y="0" width="4" height="16" fill="white" />
      {/* Background fill without stroke to avoid double border */}
      <circle cx="8" cy="8" r="8" fill="rgb(249, 250, 251)" clipPath={`url(#${clipPathId})`} />
      {/* Only draw the outer arc border */}
      <path 
        d={isRight ? "M 8 0.5 A 7.5 7.5 0 0 0 8 15.5" : "M 8 0.5 A 7.5 7.5 0 0 1 8 15.5"} 
        stroke="rgba(0, 0, 0, 0.1)" 
        strokeWidth="1" 
        fill="none" 
        strokeLinecap="round" 
      />
    </svg>
  );
};

/**
 * Custom hook to populate user contact data from EntraId authentication
 */
function useEntraIdContactData() {
  const dispatch = useDispatch();
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  const personalData = useSelector((state: RootState) => state.bookingCreation.personalData);

  useEffect(() => {
    if (!loginSuppliesContactData(tenantConfig?.authentication)) return;

    const idToken = getIdToken();
    if (!idToken) return;

    const userInfo = getEmailAndFirstAndLastName(idToken);
    if (!userInfo || userInfo.length !== 3) return;

    const [email, firstName, lastName] = userInfo;

    // Only dispatch if the field is empty to avoid overwriting user changes
    if (email && !personalData.email) {
      dispatch(emailChanged({ email }));
    }
    if (firstName && !personalData.firstName) {
      dispatch(firstNameChanged({ firstName }));
    }
    if (lastName && !personalData.lastName) {
      dispatch(lastNameChanged({ lastName }));
    }
  }, [tenantConfig, dispatch, personalData.email, personalData.firstName, personalData.lastName]);
}

/**
 * Custom hook for coupon validation logic
 */
function useCouponValidation(couponCode: string) {
  return useMemo(() => {
    // Basic coupon validation - alphanumeric with optional dashes/underscores/spaces
    const isValid = /^[a-zA-Z0-9-_ ]+$/.test(couponCode) && couponCode.trim().length > 0;
    return { isValid, isEmpty: couponCode.trim().length === 0 };
  }, [couponCode]);
}

interface BookingSummaryProps {
  loading: boolean;
}

export const BookingSummary = ({ loading }: BookingSummaryProps) => {
  const AMOUNT = 1;
  const { t, i18n: i18nHook } = useTranslation();
  const taxStatement = useTaxStatement();
  const dispatch = useDispatch();
  const uniqueId = useId();
  const { current, total } = useTemporaryStorageStepCounter("bookingSummary");
  
  // Keep moment locale in sync with i18n and derive a locale-aware format string
  useEffect(() => {
    moment.locale(i18nHook.language);
  }, [i18nHook.language]);

  const dateTimeFormat = useMemo(() => {
    const localeData = moment.localeData(i18nHook.language);
    const dateL = localeData.longDateFormat("L");
    const timeLT = localeData.longDateFormat("LT");
    // Example (de): "dd DD.MM.YYYY LT"  ->  "Mo 04.11.2025 14:00"
    // Example (en): "dd MM/DD/YYYY LT"  ->  "Mo 11/04/2025 2:00 PM"
    return `dd ${dateL} ${timeLT}`;
  }, [i18nHook.language]);
  
  // Selectors
  const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);
  const isCouponRedemptionLoading = useSelector(
    (state: RootState) => state.bookingCreation.loading.couponRedemption
  );
  const { selectedLocation, reservations } = useSelector(
    (state: RootState) => state.bookingCreation
  );
  const equipments = useSelector((state: RootState) =>
    selectedLocation ? state.bookingCreation.groupsByLocation[selectedLocation.id] : undefined
  );
  // The booked location's clock (#3219).
  const timeZone = useSelectedLocationTimeZone();

  // Local state
  const [couponCode, setCouponCode] = useState<string>("");
  const applyCoupon = useApplyCoupon();

  // Custom hooks
  useEntraIdContactData();
  const couponValidation = useCouponValidation(couponCode);

  // Memoized calculations
  const coupons = useMemo(() => findCoupons(reservations), [reservations]);
  const totalPrice = useMemo(() => calculateTotalPrice(reservations), [reservations]);
  const displayPrice = useMemo(() => calculateDisplayPrice(totalPrice, coupons), [totalPrice, coupons]);
  const consolidatedCoupons = useMemo(() => consolidateCoupons(coupons), [coupons]);
  // A promo code cannot reduce a free booking, so the field is a dead control
  // on one. ADR-0008 amendment 2026-09-16.
  const free = useMemo(
    () => isFreeOfCharge(totalPrice, reservations.length),
    [totalPrice, reservations.length]
  );
  const showCouponButton =
    !tenantConfig.disableCoupon && tenantConfig.allowCoupons && coupons.length === 0 && !free;

  // The next step routes a booking with nothing to pay to <NoPayment> rather than
  // Stripe, so promising payment here would be wrong. Same predicate as the payment
  // step uses, so label and routing cannot disagree.
  //
  // Drives the button wording ONLY. The price rows below stay gated on the tenant's
  // skipPayment flag: a free booking still shows its 0,00 € and the tax line, so an
  // external customer sees the concession stated rather than guessing what they owe.
  // ADR-0008, amendment 2026-09-14 — read it before wiring a price row to this.
  const paymentRequired = useMemo(() => requiresPayment(reservations), [reservations]);
  const nextStepCompletesBooking = tenantConfig.skipPayment || !paymentRequired;

  // Helper function to find equipment name
  const findEquipmentName = useCallback((groupId: string): string => {
    if (!equipments) return "";
    
    const targetGroup = equipments.find((group: any) => group.id === groupId);
    if (!targetGroup) return "";
    
    return getEquipmentName(targetGroup.name, targetGroup?.langNames, "title");
  }, [equipments]);

  // Event handlers
  const handleClickNext = useCallback(() => {
    dispatch(setNextButtonClick());
  }, [dispatch]);

  const handleApplyCoupon = useCallback(async () => {
    dispatch(couponRedemptionLoading({ loading: true }));

    const reservationIds = reservations.map((reservation: any) => reservation.reservationId);
    await applyCoupon(reservationIds, couponCode);
    setCouponCode("");
  }, [dispatch, reservations, applyCoupon, couponCode]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <Spinner size="lg" ariaLabel={t("common.loading")} />
      </div>
    );
  }

  const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";

  const nextButtonLabel = nextStepCompletesBooking
    ? t("temporary_storage.new_booking.booking_summary.complete_button_title")
    : t("temporary_storage.new_booking.booking_summary.next_button_title");

  return (
    <div className="flex flex-col justify-between h-full">
      {/* Step Counter */}
      <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center" role="status" aria-live="polite">
        {t("temporary_storage.new_booking.step_counter", {
          current,
          total,
          defaultValue: "Schritt {{current}} von {{total}}",
        })}
      </p>

      {/* Scrollable Content */}
      <div className="overflow-auto flex-grow">
        {/* Booking Summary Card with Ticket Design */}
        <div className="relative overflow-clip mx-5">
          {/* Ticket holes as decorative SVG half-circles */}
          <TicketHole side="right" uniqueId={uniqueId} />
          <TicketHole side="left" uniqueId={uniqueId} />
          
          <article 
            className="bg-white shadow-md flex flex-col py-8 px-5 border border-foreground/10 relative rounded-md text-foreground"
            aria-labelledby="booking-summary-title"
          >
        
            {/* Location Section */}
            <section>
              <h2 id="booking-summary-title" className="text-sm text-foreground/80">
                {t("orders.ticket_overview.location")}
              </h2>
              <p className={textBoldClass}>{selectedLocation?.name}</p>
            </section>

            {/* Booked Items Section */}
            <section className="mt-2">
              <p className="text-sm text-foreground/80">
                {t("orders.ticket_overview.booked_items")}
              </p>
            {reservations.map((reservation: any, index: number) => {
              const reservationStartDate = atStation(reservation.reservationItems[0].fromDate, timeZone);
              const reservationEndDate = atStation(reservation.reservationItems[0].toDate, timeZone);
              const equipmentName = findEquipmentName(reservation.reservationItems[0].groupId);
              const price = reservation.reservationItems[0].price;
              const duration = calculateDuration(
                reservationStartDate.toDate(),
                reservationEndDate.toDate()
              );

                return (
                  <div
                    key={reservation.id || index}
                    className={cn(
                      "w-full flex flex-col",
                      !tenantConfig.skipPayment && "mb-1.5 pb-3 border-b border-foreground/45"
                    )}
                  >
                    <div
                      className={cn(
                        textBoldClass,
                        // `items-start` + `gap-3`: the description wraps to two
                        // lines on a phone, and without a gap its second line
                        // ran up against the price. `min-w-0` lets the
                        // description actually shrink — a flex item defaults to
                        // `min-width: auto`, so it would otherwise overflow its
                        // share rather than wrap.
                        "flex w-full flex-row items-start justify-between gap-3"
                      )}
                    >
                      <span className="min-w-0 flex-1">
                      {bookedItemLabel(
                        AMOUNT,
                        equipmentName,
                        t("orders.ticket_overview.confirmation_step.locker")
                      )} -{" "}
                      {duration.days > 0 &&
                        `${duration.days} ${
                          duration.days === 1
                            ? t("general.duration_day_singular")
                            : t("general.duration_day_plural")
                        }`}
                      {duration.hours > 0 && ` ${duration.hours}${t("general.duration_hour_abbr")}`}
                      {duration.minutes > 0 && ` ${duration.minutes}${t("general.duration_minute_abbr")}`}
                    </span>
                      {!tenantConfig.skipPayment && (
                        // `shrink-0` + `whitespace-nowrap`: the price is short
                        // and must never be the thing that wraps. It was
                        // breaking as "0,00" / "€" across two lines once the
                        // description grew.
                        <span
                          className="shrink-0 whitespace-nowrap"
                          aria-label={t("general.price")}
                        >
                          {formatCurrency(price, i18n.language)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm">
                        {reservationStartDate.format(dateTimeFormat)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm">
                        {reservationEndDate.format(dateTimeFormat)}
                      </p>
                    </div>
                  </div>
              );
            })}
            </section>

            {/* Pricing Section */}
            <div>
              {/* Applied Coupons */}
              {!tenantConfig.disableCoupon && consolidatedCoupons.map((coupon) => (
                <div className="flex flex-row justify-between" key={coupon.code}>
                  <p>
                    {t("book_location.confirmation.coupon_item")} ({coupon.code})
                  </p>
                  <p className={textBoldClass}>
                    {formatCurrency(
                      (coupon.amount > totalPrice ? totalPrice : coupon.amount) * -1,
                      i18n.language
                    )}
                  </p>
                </div>
              ))}

              {/* Total Price */}
              {!tenantConfig.skipPayment && (
                <div className="flex flex-row justify-between">
                  <p>{t("book_location.confirmation.total_items")}</p>
                  <p className={textBoldClass}>
                    {formatCurrency(displayPrice, i18n.language)}
                  </p>
                </div>
              )}

              {/* Coupon Input */}
              {showCouponButton && (
                <div className="flex flex-row justify-between items-center gap-4 my-2">
                  <Input
                    type="text"
                    placeholder={t("general.enter_coupon_code")}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && couponValidation.isValid) {
                        void handleApplyCoupon();
                      }
                    }}
                    inputSize="sm"
                    className="w-full"
                    aria-label={t("general.enter_coupon_code")}
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    disabled={!couponValidation.isValid || isCouponRedemptionLoading}
                    variant="primary"
                    size="sm"
                    aria-busy={isCouponRedemptionLoading}
                  >
                    {t("orders.ticket_overview.button")}
                  </Button>
                </div>
              )}

              {/* Tax Information — omitted on a free booking: there is no
                  price for it to be a statement about. The price rows above
                  stay, per ADR-0008's 2026-09-14 amendment. */}
              {!tenantConfig.skipPayment && !free && (
                <p className="text-sm">
                  {taxStatement.sentence}
                </p>
              )}
            </div>

            {isCouponRedemptionLoading && <SpinnerOverlay />}
          </article>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 w-full max-w-md px-4 z-10">
        <Button
          onClick={handleClickNext}
          variant="primary"
          size="lg"
          className="w-full"
          aria-label={nextButtonLabel}
        >
          {nextButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default memo(BookingSummary);
